import { z } from "zod";
import { NotificationType } from "@/components/practice/notifications/notification-display";

/**
 * Per-practice routing for automated in-app notifications: for each notification type, whether it
 * is on, and who receives it - groups of the practice's team, specific teammates, or both. Set on
 * Settings → Notification Center, stored on the practice as JSON, and read by the emit engine
 * (`background/notifications/emit-notification.ts`).
 *
 * In-app notifications only. Emails (e.g. the inbound-fax email) keep their own recipients.
 */

// ---------------------------------------------------------------------------------------------
//                                      RECIPIENTS
// ---------------------------------------------------------------------------------------------

/**
 * The groups a notification type can route to.
 *
 * - `all` — every active provider and staff member in the practice.
 * - `admin` — the practice's non-clinical admin staff (the Admin role, `VibrantRoles.Admin`).
 * - `primary-provider` — the patient's primary provider (`Patient.generalPractitioner[0]`).
 * - `care-team` — the patient's care team, plus the primary provider.
 * - `appointment-provider` — the provider on the appointment. Appointment types only.
 *
 * A route names any mix of these groups and specific teammates, and sends to all of them. A route
 * that resolves to nobody (no patient, no primary provider, empty care team, no provider on the
 * appointment, no teammate still in the practice) falls back to the provider on the appointment
 * when the event has one, and otherwise to `admin`.
 */
export const NOTIFICATION_GROUPS = [
  "all",
  "admin",
  "primary-provider",
  "care-team",
  "appointment-provider",
] as const;
export type NotificationGroup = (typeof NOTIFICATION_GROUPS)[number];

export const NOTIFICATION_GROUP_LABELS: Record<NotificationGroup, string> = {
  all: "All",
  admin: "Admin",
  "primary-provider": "Primary Provider",
  "care-team": "Full Care Team",
  "appointment-provider": "Provider on Appointment",
};

// ---------------------------------------------------------------------------------------------
//                                    ROUTED TYPES
// ---------------------------------------------------------------------------------------------

/** The notification types an automated producer emits, in settings-page order. */
export const ROUTED_NOTIFICATION_TYPES = [
  NotificationType.FaxReceived,
  NotificationType.LabsReceived,
  NotificationType.RecordUploaded,
  NotificationType.QuestionnaireCompleted,
  NotificationType.AppointmentBooked,
  NotificationType.AppointmentCancelled,
  NotificationType.AppointmentRescheduled,
] as const;
export type RoutedNotificationType = (typeof ROUTED_NOTIFICATION_TYPES)[number];

// An inbound fax arrives unmatched, so the patient groups would name nobody.
const FAX_GROUPS = ["all", "admin"] as const satisfies readonly NotificationGroup[];

const PATIENT_GROUPS = [
  "all",
  "admin",
  "primary-provider",
  "care-team",
] as const satisfies readonly NotificationGroup[];

const APPOINTMENT_GROUPS = [
  ...PATIENT_GROUPS,
  "appointment-provider",
] as const satisfies readonly NotificationGroup[];

/** The groups each type offers, in menu order. The write schema rejects anything else. */
export const ALLOWED_NOTIFICATION_GROUPS: Record<
  RoutedNotificationType,
  readonly NotificationGroup[]
> = {
  [NotificationType.FaxReceived]: FAX_GROUPS,
  [NotificationType.LabsReceived]: PATIENT_GROUPS,
  [NotificationType.RecordUploaded]: PATIENT_GROUPS,
  [NotificationType.QuestionnaireCompleted]: PATIENT_GROUPS,
  [NotificationType.AppointmentBooked]: APPOINTMENT_GROUPS,
  [NotificationType.AppointmentCancelled]: APPOINTMENT_GROUPS,
  [NotificationType.AppointmentRescheduled]: APPOINTMENT_GROUPS,
};

/** Types a practice can turn off. An inbound fax can't be - someone must match it to a patient. */
export function canTurnOffNotificationType(type: RoutedNotificationType): boolean {
  return type !== NotificationType.FaxReceived;
}

// ---------------------------------------------------------------------------------------------
//                                   SCHEMA + PARSING
// ---------------------------------------------------------------------------------------------

/**
 * One type's route: on or off, and who receives it - any mix of the type's groups and specific
 * teammates (`Practitioner/<id>` refs). A route always names someone.
 */
function routeSchema<const Groups extends readonly [NotificationGroup, ...NotificationGroup[]]>(
  groups: Groups,
  enabled: z.ZodType<boolean>,
) {
  return z
    .object({
      enabled,
      groups: z.array(z.enum(groups)),
      teammateRefs: z.array(z.string().startsWith("Practitioner/")),
    })
    .refine((route) => route.groups.length + route.teammateRefs.length > 0, {
      message: "A route must name at least one group or teammate",
    });
}

/** Every routed type is required on write, so a saved config never depends on the defaults. */
export const notificationRoutingConfigSchema = z.object({
  [NotificationType.FaxReceived]: routeSchema(FAX_GROUPS, z.literal(true)),
  [NotificationType.LabsReceived]: routeSchema(PATIENT_GROUPS, z.boolean()),
  [NotificationType.RecordUploaded]: routeSchema(PATIENT_GROUPS, z.boolean()),
  [NotificationType.QuestionnaireCompleted]: routeSchema(PATIENT_GROUPS, z.boolean()),
  [NotificationType.AppointmentBooked]: routeSchema(APPOINTMENT_GROUPS, z.boolean()),
  [NotificationType.AppointmentCancelled]: routeSchema(APPOINTMENT_GROUPS, z.boolean()),
  [NotificationType.AppointmentRescheduled]: routeSchema(APPOINTMENT_GROUPS, z.boolean()),
});

/** Each routed type's route. Each type holds only groups it allows. */
export type NotificationRoutingConfig = z.infer<typeof notificationRoutingConfigSchema>;
export type NotificationRoute = NotificationRoutingConfig[RoutedNotificationType];

const toGroup = <Group extends NotificationGroup>(group: Group) => ({
  enabled: true as const,
  groups: [group],
  teammateRefs: [],
});

/**
 * Routing for a type the practice has never saved on the settings page. A practice that saved a
 * route keeps it, so a change here reaches only the types it never saved. An inbound fax goes to the
 * whole team. The appointment types go to the appointment's own provider. The engine falls back
 * when a route names nobody (see `NOTIFICATION_GROUPS`).
 */
export const DEFAULT_NOTIFICATION_ROUTING: NotificationRoutingConfig = {
  [NotificationType.FaxReceived]: toGroup("all"),
  [NotificationType.LabsReceived]: toGroup("care-team"),
  [NotificationType.RecordUploaded]: toGroup("primary-provider"),
  [NotificationType.QuestionnaireCompleted]: toGroup("primary-provider"),
  [NotificationType.AppointmentBooked]: toGroup("appointment-provider"),
  [NotificationType.AppointmentCancelled]: toGroup("appointment-provider"),
  [NotificationType.AppointmentRescheduled]: toGroup("appointment-provider"),
};

const storedBlobSchema = z.record(z.string(), z.unknown());

export interface ParsedNotificationRouting {
  config: NotificationRoutingConfig;
  /**
   * Types whose saved route no longer parses, so they run on the default instead of the practice's
   * choice. A saved blob that is not a JSON object counts every type. A type the practice never
   * saved (one added later) is not listed - its default is expected.
   */
  invalidTypes: RoutedNotificationType[];
}

/**
 * Reads the stored JSON blob into a complete config. A missing blob yields the defaults. Per type, a
 * missing or no-longer-valid route takes its default - so a type added after the practice last
 * saved still routes. `invalidTypes` names the saved routes that fell back, so the caller can warn.
 */
export function readNotificationRouting(raw: string | undefined | null): ParsedNotificationRouting {
  if (!raw?.length) return { config: DEFAULT_NOTIFICATION_ROUTING, invalidTypes: [] };
  const parsed = storedBlobSchema.safeParse(parseJson(raw));
  if (!parsed.success) {
    return { config: DEFAULT_NOTIFICATION_ROUTING, invalidTypes: [...ROUTED_NOTIFICATION_TYPES] };
  }

  const config: Record<string, unknown> = {};
  const invalidTypes: RoutedNotificationType[] = [];
  for (const type of ROUTED_NOTIFICATION_TYPES) {
    const saved =
      type === NotificationType.FaxReceived
        ? withoutRetiredFaxGroups(parsed.data[type])
        : parsed.data[type];
    const choice = notificationRoutingConfigSchema.shape[type].safeParse(saved);
    if (!choice.success && saved !== undefined) invalidTypes.push(type);
    config[type] = choice.success ? choice.data : DEFAULT_NOTIFICATION_ROUTING[type];
  }
  // every key now holds an allowed choice, so this parse can't throw
  return { config: notificationRoutingConfigSchema.parse(config), invalidTypes };
}

/** The complete config only. Use `readNotificationRouting` where a fallback must be reported. */
export function parseNotificationRoutingConfig(
  raw: string | undefined | null,
): NotificationRoutingConfig {
  return readNotificationRouting(raw).config;
}

// Groups the fax route offered once. They named nobody for an unmatched fax, which fell back to admins.
const RETIRED_FAX_GROUPS: unknown[] = ["primary-provider", "care-team"];

/**
 * A saved fax route without the groups it no longer offers. When nothing is left, the route goes to
 * the whole team, the fax default. This keeps the rest of a saved route, and it keeps a saved route
 * that names these groups from reading as invalid. Input of another shape passes through for the
 * schema to judge.
 */
function withoutRetiredFaxGroups(saved: unknown): unknown {
  const route = savedRouteShape.safeParse(saved);
  if (!route.success) return saved;
  const groups = route.data.groups.filter((group) => !RETIRED_FAX_GROUPS.includes(group));
  if (groups.length === route.data.groups.length) return saved;
  const isEmpty = groups.length + route.data.teammateRefs.length === 0;
  return { ...route.data, groups: isEmpty ? ["all"] : groups };
}

// Only the lists the fax cleanup reads. Every other key passes through untouched.
const savedRouteShape = z.looseObject({
  groups: z.array(z.unknown()),
  teammateRefs: z.array(z.unknown()),
});

/** JSON.parse that yields undefined for malformed input. */
function parseJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}
